import React, {useCallback, useEffect, useState} from 'react';
import {round} from 'lodash';
import {EnrichedAstNode, visitorEnriched} from 'rendition-core';
import {useNodeChildrenIds} from 'Hooks/AstHooks/useNodeChildren';
import {useViewStore} from 'stores/view';
import {usePreviewContext} from 'Contexts/PreviewContext';
import TabbedButtonToggle from './Tailwind/TabbedButtonToggle';
import classNames from 'classnames';
import {useHighlighterContext} from 'Contexts/HighlighterContext';
import {useProductContext} from 'Contexts/ProductContext';

interface Props {
    resizable?: boolean;
    selectedRootNode: EnrichedAstNode;
}

interface ToggleProps {
    selectedRootNode: EnrichedAstNode;
}

const ALL = 'ALL';
const ABS = 'ABS';
const FLEX = 'FLEX';
type Visibility = typeof ALL | typeof ABS | typeof FLEX;

const DisplayOptions = [
    {
        icon: 'All',
        value: 'ALL',
    },
    {
        icon: 'ABS',
        value: 'ABS',
    },
    {
        icon: 'FLEX',
        value: 'FLEX',
    },
];

export const VisibilityToggle: React.FC<ToggleProps> = ({selectedRootNode}) => {
    const {mode} = useProductContext();
    const [visibility, setVisibility] = useState<Visibility>(mode === 'Rendition_Code' ? ALL : FLEX);
    const {setHiddenNodes, setTranslucentNodes} = usePreviewContext();
    const {flexNodes, absolutePositionedNodes} = useNodeChildrenIds(selectedRootNode);

    useEffect(() => {
        if (visibility === ABS) {
            setHiddenNodes(flexNodes);
            setTranslucentNodes([]);
        } else if (visibility === FLEX) {
            setHiddenNodes([]);
            setTranslucentNodes(absolutePositionedNodes);
        } else {
            setHiddenNodes([]);
            setTranslucentNodes([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibility]);

    if (mode === 'Rendition_Code') {
        return null;
    }

    return (
        <div id="rendition-visibility">
            <h1 className="pb-1 text-[10px] font-bold text-white">Visibility</h1>
            <TabbedButtonToggle
                items={DisplayOptions}
                value={visibility}
                onClick={(item) => setVisibility(item as Visibility)}
            />
        </div>
    );
};

export const CanvasTopMenu: React.FC<Props> = ({resizable = true, selectedRootNode}) => {
    const [showHighlights, setShowHighlights] = useState(false);
    const previewSize = useViewStore.use.previewWidth();
    const setPreviewSize = useViewStore.use.setPreviewWidth();
    const {addNodeHighlight, view, clearHighlightByType} = useHighlighterContext();
    const {mode} = useProductContext();

    const turnOffHighlights = useCallback(() => {
        if (showHighlights) {
            setShowHighlights(false);
            clearHighlightByType('PaddingHorizontal');
            clearHighlightByType('MarginHorizontal');
        }
    }, [clearHighlightByType, showHighlights]);

    useEffect(() => {
        if (showHighlights && mode === 'Rendition_Code') {
            visitorEnriched(view.tree, (node) => {
                addNodeHighlight(node.internalId, 'PaddingHorizontal');
                addNodeHighlight(node.internalId, 'MarginHorizontal');
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showHighlights, mode]);

    return (
        <div
            className={classNames(
                'absolute left-1/2 z-20 h-40 w-[300px] translate-x-[-50%] items-center justify-start gap-4 rounded-bl-2xl rounded-br-2xl border border-solid border-neutral-700 bg-neutral-900 px-4 py-2',
                'flex flex-col py-0 min-[1361px]:h-20 min-[1361px]:w-[700px] min-[1361px]:flex-row min-[1361px]:gap-16'
            )}
        >
            <VisibilityToggle selectedRootNode={selectedRootNode} />
            {resizable && (
                <div id="rendition-screen-size" className="flex flex-grow flex-col gap-3">
                    <h1 className="text-[10px] font-bold text-white">Screen Size</h1>
                    <div className="flex flex-row items-center gap-2">
                        <div className="flex w-20 flex-row items-end">
                            <h2 className="text-[20px] font-extrabold leading-6 text-white">
                                {previewSize > 1000 ? round(previewSize / 1000, 1) + 'K' : previewSize}
                            </h2>
                            <h2 className="text-[13px] font-extrabold leading-5 text-zinc-600">px</h2>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="2000"
                            value={previewSize}
                            onMouseUp={turnOffHighlights}
                            onBlur={turnOffHighlights}
                            onChange={(e) => {
                                setShowHighlights(true);
                                // reset();
                                setPreviewSize(parseInt(e.target.value));
                            }}
                            className="mousetrap range-thumb range h-7 w-full cursor-pointer appearance-none rounded-2xl bg-zinc-600"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
